import FocusChooser from './FocusChooser';
import { Outlet, NavLink, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import {
  Sun,
  Target,
  Users,
  FolderKanban,
  Compass,
  Library,
  Archive,
  CheckSquare,
  Calendar,
  FileDown,
  Settings,
  LogOut,
  Mail,
  Menu,
  X,
  Search,
  Sparkles,
  Plug,
  MessageSquarePlus,
  BrainCircuit,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { startPendingSender } from '../services/pendingCaptures';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { screenOfPath } from '@cerebro-diem/core';
import { useAuthStore } from '../stores/authStore';
import { useFeedbackDialog } from '../stores/feedbackStore';
import { useTimeZoneSetup } from '../stores/timeZoneStore';
import { errorMessage } from '../services/errors';
import { hasSeenGuide } from '../services/guide';
import { setInitialTimeZone } from '../services/profile';
import { queryKeys, refreshFilingViews } from '../services/queries';
import { invokeEdgeFunction } from '../services/edgeFunctions';
import CaptureInput from './CaptureInput';
import FeedbackDialog from './FeedbackDialog';
import { iconButton } from './styles';
import { isSectionRootNavigation, sectionRootNavigation } from './sectionRootNavigation';

type NavItem = { to: string; icon: typeof Sun; label: string };

/** Today; then PARA together (Projects, Areas, Resources and the Archive); then People and the other lists. */
const navGroups: { label?: string; items: NavItem[] }[] = [
  {
    items: [
      { to: '/', icon: Sun, label: 'Today' },
      { to: '/brain-dump', icon: BrainCircuit, label: 'Brain dump' },
      { to: '/outcomes', icon: Target, label: 'What matters' },
    ],
  },
  {
    label: 'PARA',
    items: [
      { to: '/projects', icon: FolderKanban, label: 'Projects' },
      { to: '/areas', icon: Compass, label: 'Areas' },
      { to: '/resources', icon: Library, label: 'Resources' },
      { to: '/archive', icon: Archive, label: 'Archive' },
    ],
  },
  {
    items: [
      { to: '/people', icon: Users, label: 'People' },
      { to: '/tasks', icon: CheckSquare, label: 'All tasks' },
      { to: '/search', icon: Search, label: 'Search' },
      { to: '/patterns', icon: Sparkles, label: 'Patterns' },
      { to: '/calendar', icon: Calendar, label: 'Calendar' },
      { to: '/digest', icon: Mail, label: 'Digest' },
      { to: '/import', icon: FileDown, label: 'Import' },
      { to: '/integrations', icon: Plug, label: 'Integrations' },
    ],
  },
];

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
    isActive ? 'bg-primary-container text-primary' : 'text-on-surface-variant hover:bg-surface-variant'
  }`;

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { signOut, user } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setZoneProblem = useTimeZoneSetup((state) => state.setProblem);
  const location = useLocation();
  const navigationType = useNavigationType();
  const [pageReset, setPageReset] = useState(0);
  const pageRef = useRef<HTMLDivElement>(null);

  // Only an accepted explicit sidebar navigation resets the page. Query/hash changes and
  // history navigation retain their existing page state; Layout and its capture sender stay mounted.
  useEffect(() => {
    if (navigationType === 'POP' || !isSectionRootNavigation(location.state)) return;
    setPageReset((reset) => reset + 1);
    if (pageRef.current) pageRef.current.scrollTop = 0;
  }, [location.key, location.state, navigationType]);
  const feedback = useFeedbackDialog();

  // The first sign-in gives the profile this browser's time zone; a zone
  // already set is kept. When that cannot happen, Settings shows why, beside the
  // unset zone (digests and reminders are off until it is set), and offers this browser's zone.
  useEffect(() => {
    if (!user?.id) return;
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!zone) {
      setZoneProblem('This browser did not report a time zone; choose yours below.');
      return;
    }
    setInitialTimeZone(zone).then(
      () => {
        setZoneProblem(null);
        return queryClient.invalidateQueries({ queryKey: queryKeys.profile });
      },
      (err: unknown) => setZoneProblem(`This browser's time zone (${zone}) could not be set: ${errorMessage(err)}.`),
    );
  }, [user?.id, queryClient, setZoneProblem]);

  // Captures this browser still holds (made offline, or whose save failed) are sent from here on every
  // page: now, with backoff, and when the browser is back online. Each is filed once, when it is saved.
  useEffect(() => {
    if (!user?.id) return;
    return startPendingSender((captureId) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.captures });
      invokeEdgeFunction('classify-capture', { capture_id: captureId })
        .catch(() => undefined) // Not filed: it waits in the Inbox, where it says so.
        .finally(() => void refreshFilingViews(queryClient));
    });
  }, [user?.id, queryClient]);

  // The first time a user reaches Today in this browser, show how Cerebro works (once; the page marks it seen).
  useEffect(() => {
    if (user && location.pathname === '/' && !hasSeenGuide(user.id)) {
      navigate('/how-it-works', { replace: true });
    }
  }, [user, location.pathname, navigate]);

  // A refused sign-out leaves the user signed in (the store keeps the session), so say so here.
  const signOutMutation = useMutation({ mutationFn: signOut, onSuccess: () => navigate('/login') });

  const openFeedback = () => {
    setSidebarOpen(false);
    feedback.open(screenOfPath(location.pathname));
  };

  // The drawer (below lg): Escape closes it, and focus goes into it when it opens and back to the menu button when it closes.
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const drawerWasOpen = useRef(false);
  useEffect(() => {
    if (!sidebarOpen) {
      if (drawerWasOpen.current) menuButtonRef.current?.focus();
      drawerWasOpen.current = false;
      return;
    }
    drawerWasOpen.current = true;
    closeButtonRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebarOpen]);

  return (
    // h-dvh: the screen the browser leaves visible (h-screen is the fallback where dvh is unknown).
    // The side padding keeps a sideways iPhone's content clear of the notch.
    <div className="flex h-screen h-dvh bg-background pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
      {/* Sidebar: a drawer below lg, always shown from lg. Closed, it is invisible, so it cannot be tabbed into. */}
      <aside
        id="app-nav"
        aria-label="Menu"
        className={`
          fixed inset-y-0 left-0 z-40 w-64 max-w-[85vw] bg-surface shadow-xl transform transition-[transform,visibility] duration-200 ease-in-out
          pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]
          lg:relative lg:z-auto lg:max-w-none lg:translate-x-0 lg:visible lg:p-0
          ${sidebarOpen ? 'translate-x-0 visible' : '-translate-x-full invisible'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 pl-6 pr-2 py-5 lg:px-6 border-b border-surface-variant">
            <div className="w-10 h-10 shrink-0 rounded-xl bg-primary flex items-center justify-center">
              <span className="text-on-primary font-bold text-lg">CD</span>
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="font-bold text-lg text-on-surface">Cerebro Diem</h1>
              <p className="text-xs text-on-surface-variant">Seize Your Thoughts</p>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close menu"
              className={`lg:hidden ${iconButton} p-2.5 rounded-xl text-on-surface-variant hover:bg-surface-variant`}
            >
              <X size={24} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 overflow-y-auto">
            {navGroups.map((group, index) => (
              <div key={group.label ?? index} className={index > 0 ? 'mt-3 pt-3 border-t border-surface-variant' : undefined}>
                {group.label && (
                  <p className="px-4 pb-1 text-xs font-semibold uppercase tracking-wide text-on-surface-variant">{group.label}</p>
                )}
                <div className="space-y-1">
                  {group.items.map(({ to, icon: Icon, label }) => (
                    <NavLink key={to} to={to} state={sectionRootNavigation} onClick={() => setSidebarOpen(false)} className={navLinkClass}>
                      <Icon size={20} />
                      <span className="font-medium">{label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Bottom section */}
          <div className="p-4 border-t border-surface-variant">
            <button
              type="button"
              onClick={openFeedback}
              className="flex items-center gap-3 px-4 py-3 w-full rounded-xl text-on-surface-variant hover:bg-surface-variant transition-colors"
            >
              <MessageSquarePlus size={20} />
              <span className="font-medium">Send feedback</span>
            </button>

            <NavLink to="/settings" state={sectionRootNavigation} onClick={() => setSidebarOpen(false)} className={(state) => `${navLinkClass(state)} mb-2`}>
              <Settings size={20} />
              <span className="font-medium">Settings</span>
            </NavLink>

            <div className="flex items-center gap-3 px-4 py-2 text-sm text-on-surface-variant">
              <div className="w-8 h-8 rounded-full bg-primary-container flex items-center justify-center">
                <span className="text-primary font-medium">
                  {user?.email?.[0].toUpperCase()}
                </span>
              </div>
              <span className="flex-1 truncate">{user?.email}</span>
            </div>

            <button
              type="button"
              onClick={() => signOutMutation.mutate()}
              disabled={signOutMutation.isPending}
              className="flex items-center gap-3 px-4 py-3 w-full rounded-xl text-error hover:bg-error/10 transition-colors"
            >
              <LogOut size={20} />
              <span className="font-medium">Sign Out</span>
            </button>
            {signOutMutation.isError && (
              <p role="alert" className="px-4 text-sm text-error">
                Not signed out: {errorMessage(signOutMutation.error)}
              </p>
            )}
          </div>
        </div>
      </aside>

      {/* Behind the open drawer: a tap here closes it. */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-scrim/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content: a column of the top bar (below lg), the page, and the capture bar. min-w-0 lets it be narrower than a long word. */}
      <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* The top bar below lg: the menu button sits in it, above the page, so it never covers a page's title. */}
        <header className="lg:hidden shrink-0 flex items-center gap-2 px-2 py-1 pt-[max(0.25rem,env(safe-area-inset-top))] bg-surface border-b border-surface-variant">
          <button
            ref={menuButtonRef}
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
            aria-controls="app-nav"
            aria-expanded={sidebarOpen}
            className={`${iconButton} p-2.5 rounded-xl text-on-surface hover:bg-surface-variant`}
          >
            <Menu size={24} />
          </button>
          <span className="font-bold text-on-surface">Cerebro Diem</span>
        </header>

        {/* The page. The bottom padding leaves the last line clear of the Feedback button. */}
        <div ref={pageRef} className="flex-1 overflow-y-auto px-4 pt-4 pb-20 sm:px-6 sm:pt-6">
          <Outlet key={pageReset} />
        </div>
        <FocusChooser />

        {/* The capture bar: in the column, not over the page, so it grows with its messages without covering anything. */}
        <footer className="relative z-20 shrink-0 bg-surface border-t border-surface-variant px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {/* Feedback, one tap from every page: just above the capture bar, bottom right. */}
          <button
            type="button"
            onClick={openFeedback}
            aria-label="Send feedback"
            title="Send feedback"
            className={`absolute right-4 bottom-full mb-3 ${iconButton} gap-2 rounded-full bg-surface px-3 py-2 text-sm font-medium text-primary shadow-lg border border-outline/20 hover:bg-primary-container`}
          >
            <MessageSquarePlus size={18} />
            <span className="hidden sm:inline">Feedback</span>
          </button>
          <CaptureInput />
        </footer>
      </main>

      {feedback.screen !== null && <FeedbackDialog screen={feedback.screen} onClose={feedback.close} />}
    </div>
  );
}
